import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Link,
} from '@mui/material';
import {
  AddOutlined,
  ArrowBackOutlined,
  CameraswitchOutlined,
  DownloadOutlined,
  DeleteOutlined,
  DescriptionOutlined,
  ExpandMoreOutlined,
  LinkOffOutlined,
  OpenInNewOutlined,
  PhoneIphoneOutlined,
  RefreshOutlined,
  LaptopMacOutlined,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';
import { JourneyStepper, SectionCard } from '../../design-system';
import { downloadFileFromUrl } from '../../lib/downloadFile';
import { PORTAL_ROUTES } from '../../routes';
import { listFamilyMembers, uploadFileToPresignedUrl } from '../../services/familyApi';
import { createDocument, createDocumentUpload, deleteDocument, listDocuments } from '../../services/documentApi';
import {
  createLivePhotoLink,
  finalizeLivePhotoLink,
  getLivePhotoLinkStatus,
} from '../../services/livePhotoApi';
import {
  attachTripRequirementDocument,
  createTripPackage,
  createTrip,
  deleteTrip,
  deleteTripPackage,
  getTrip,
  getTripPackageDownloadUrl,
  getTripRequirementTemplateDownloadUrl,
  listTrips,
  removeTripRequirementDocument,
  startTripRequirementAiValidation,
} from '../../services/tripApi';
import DocumentDetailsDialog, { formatDocumentType } from './components/DocumentDetailsDialog';

const DESTINATION_OPTIONS = [
  { code: 'NL', label: 'The Netherlands', available: true },
  { code: 'GR', label: 'Greece', available: false },
  { code: 'AT', label: 'Austria', available: false },
  { code: 'BE', label: 'Belgium', available: false },
  { code: 'HR', label: 'Croatia', available: false },
  { code: 'CZ', label: 'Czech Republic', available: false },
  { code: 'DK', label: 'Denmark', available: false },
  { code: 'EE', label: 'Estonia', available: false },
  { code: 'FI', label: 'Finland', available: false },
  { code: 'FR', label: 'France', available: false },
  { code: 'DE', label: 'Germany', available: false },
  { code: 'HU', label: 'Hungary', available: false },
  { code: 'IS', label: 'Iceland', available: false },
  { code: 'IT', label: 'Italy', available: false },
  { code: 'LV', label: 'Latvia', available: false },
  { code: 'LI', label: 'Liechtenstein', available: false },
  { code: 'LT', label: 'Lithuania', available: false },
  { code: 'LU', label: 'Luxembourg', available: false },
  { code: 'MT', label: 'Malta', available: false },
  { code: 'NO', label: 'Norway', available: false },
  { code: 'PL', label: 'Poland', available: false },
  { code: 'PT', label: 'Portugal', available: false },
  { code: 'SK', label: 'Slovakia', available: false },
  { code: 'SI', label: 'Slovenia', available: false },
  { code: 'ES', label: 'Spain', available: false },
  { code: 'SE', label: 'Sweden', available: false },
  { code: 'CH', label: 'Switzerland', available: false },
];

const VISA_TYPE_OPTIONS = [
  { value: 'SchengenTourism', label: 'Short-stay Schengen visa (tourism)', available: true },
  { value: 'SchengenBusiness', label: 'Short-stay Schengen visa (business or official visit)', available: false },
  { value: 'SchengenFamilyVisit', label: 'Short-stay Schengen visa (family or friend visit)', available: false },
  { value: 'SchengenTransit', label: 'Airport transit visa', available: false },
];

const languageNameFormatter = typeof Intl !== 'undefined' && typeof Intl.DisplayNames === 'function'
  ? new Intl.DisplayNames(['en'], { type: 'language' })
  : null;

const initialForm = {
  destinationCountryCode: 'NL',
  departureDate: '',
  returnDate: '',
  travelers: {},
  documentSelections: {},
};

function formatMemberName(member) {
  return [member.firstName, member.middleNames, member.lastName].filter(Boolean).join(' ');
}

function formatVisaType(value) {
  return VISA_TYPE_OPTIONS.find((option) => option.value === value)?.label ?? value;
}

function formatTripDateRange(trip) {
  return trip.returnDate
    ? `${trip.departureDate} to ${trip.returnDate}`
    : `Departure ${trip.departureDate}`;
}

function formatRequirementType(value) {
  return {
    OfflineForm: 'Offline form',
    OnlineForm: 'Online form',
    Appointment: 'Appointment',
    Photo: 'Photo',
    Document: 'Document',
  }[value] ?? value ?? 'Requirement';
}

function formatDateTime(value) {
  if (!value) {
    return 'Unknown';
  }

  const parsedValue = new Date(value);
  if (Number.isNaN(parsedValue.getTime())) {
    return value;
  }

  return parsedValue.toLocaleString();
}

function isImageDocument(document) {
  return Boolean(document?.contentType?.startsWith('image/'));
}

function isProbablyMobileDevice() {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') {
    return false;
  }

  const userAgent = navigator.userAgent || navigator.vendor || window.opera || '';
  const coarsePointer = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;

  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(userAgent) || coarsePointer;
}

function formatPackageDocumentsSummary(packageItem) {
  const names = packageItem?.sourceDocumentNames ?? [];
  if (!names.length) {
    return 'No documents captured';
  }

  return names.join(', ');
}

function truncatePackageDocuments(packageItem, limit = 2) {
  const names = packageItem?.sourceDocumentNames ?? [];
  if (names.length <= limit) {
    return formatPackageDocumentsSummary(packageItem);
  }

  return `${names.slice(0, limit).join(', ')} +${names.length - limit} more`;
}

function formatLanguageName(value) {
  if (!value) {
    return '';
  }

  const normalizedValue = String(value).trim();
  if (!normalizedValue) {
    return '';
  }

  const formattedName = languageNameFormatter?.of(normalizedValue.toLowerCase());
  if (formattedName) {
    return formattedName;
  }

  return normalizedValue.toUpperCase() === normalizedValue
    ? normalizedValue
    : normalizedValue.charAt(0).toUpperCase() + normalizedValue.slice(1);
}

function formatSizeValue(value) {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  return `${value} cm`;
}

function parseNumericValue(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function getPhotoAspectRatio(metadata) {
  const width = parseNumericValue(metadata?.size_x);
  const height = parseNumericValue(metadata?.size_y);

  if (!width || !height) {
    return 3.5 / 4.5;
  }

  return width / height;
}

function getRequirementAiValidations(requirement) {
  const validations = requirement?.ai_validations ?? requirement?.aiValidations ?? [];
  return Array.isArray(validations) ? validations : [];
}

function getLatestRequirementAiValidation(requirement) {
  const validations = getRequirementAiValidations(requirement)
    .filter((validation) => validation?.timestamp && validation?.verdict)
    .slice()
    .sort((left, right) => new Date(right.timestamp).getTime() - new Date(left.timestamp).getTime());

  return validations[0] ?? null;
}

function requirementSupportsAiValidation(requirement) {
  return ['Photo', 'Document', 'OfflineForm'].includes(requirement?.type ?? '');
}

function getRequirementAiValidationStatusLabel(requirement, isRunning = false) {
  if (isRunning) {
    return 'AI Validation Running';
  }

  const latestValidation = getLatestRequirementAiValidation(requirement);
  if (!latestValidation) {
    return 'Pending AI Validation';
  }

  return latestValidation.verdict === 'passed' ? 'AI Validation Succeeded' : 'AI Validation Failed';
}

function getRequirementAiValidationStatusColor(requirement, isRunning = false) {
  if (isRunning) {
    return 'info';
  }

  const latestValidation = getLatestRequirementAiValidation(requirement);
  if (!latestValidation) {
    return 'warning';
  }

  return latestValidation.verdict === 'passed' ? 'success' : 'error';
}

function getRequirementAiValidationRationale(requirement) {
  return getLatestRequirementAiValidation(requirement)?.rationale ?? '';
}

function getRequirementAiValidationTask(tasks, tripId, requirement) {
  if (!tripId || !requirement?.memberId || !requirement?.requirementUuid) {
    return null;
  }

  return tasks.find(
    (task) =>
      task.tripId === tripId &&
      task.memberId === requirement.memberId &&
      task.requirementUuid === requirement.requirementUuid &&
      (task.status === 'pending' || task.status === 'running')
  ) ?? null;
}

function getTemplateDocumentId(templateDocument) {
  return templateDocument?.id ?? templateDocument?.key ?? '';
}

function TripCard({ members, onDelete, onClick, trip }) {
  return (
    <Box
      onClick={() => onClick(trip.tripId)}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick(trip.tripId);
        }
      }}
      sx={{
        bgcolor: 'background.paper',
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: 1.5,
        boxShadow: '0 2px 8px rgba(14, 28, 45, 0.04)',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        minHeight: 132,
        p: 2,
        cursor: 'pointer',
        transition: 'border-color 120ms ease, box-shadow 120ms ease, transform 120ms ease',
        '&:hover': {
          borderColor: 'primary.main',
          boxShadow: '0 10px 24px rgba(14, 28, 45, 0.08)',
          transform: 'translateY(-1px)',
        },
      }}
    >
      <Stack alignItems="flex-start" direction="row" justifyContent="space-between" spacing={1}>
        <Box sx={{ minWidth: 0 }}>
          <Typography noWrap sx={{ fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.2 }}>
            {trip.destinationCountryName}
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.45 }} variant="body2">
            {formatTripDateRange(trip)}
          </Typography>
        </Box>
        <Stack alignItems="center" direction="row" spacing={0.5}>
          <Chip label={`${trip.travelers.length} traveler${trip.travelers.length === 1 ? '' : 's'}`} size="small" />
          <Tooltip title="Delete trip">
            <span>
              <IconButton
                aria-label={`delete trip to ${trip.destinationCountryName}`}
                color="default"
                onClick={(event) => {
                  event.stopPropagation();
                  onDelete(trip);
                }}
                size="small"
              >
                <DeleteOutlined fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>
      <Stack spacing={0.45} sx={{ minWidth: 0 }}>
        {trip.travelers.map((traveler) => {
          const member = members.find((item) => item.memberId === traveler.memberId);
          return (
            <Typography color="text.secondary" key={`${trip.tripId}-${traveler.memberId}`} noWrap variant="caption">
              {formatMemberName(member ?? {}) || 'Traveler'} • {formatVisaType(traveler.visaType)} •{' '}
              {traveler.documentIds.length} document{traveler.documentIds.length === 1 ? '' : 's'}
            </Typography>
          );
        })}
      </Stack>
    </Box>
  );
}

function AddTripTile({ onClick }) {
  return (
    <Button
      onClick={onClick}
      sx={{
        alignItems: 'center',
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: 1.5,
        color: 'text.primary',
        display: 'flex',
        gap: 1.5,
        justifyContent: 'space-between',
        minHeight: 132,
        p: 2,
        textAlign: 'left',
        width: '100%',
      }}
      variant="outlined"
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.2 }}>
          Add new trip
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 0.2 }} variant="body2">
          Create another visa journey
        </Typography>
      </Box>
      <Avatar
        sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', height: 46, width: 46 }}
      >
        <AddOutlined />
      </Avatar>
    </Button>
  );
}

function WizardSelectOption({ option }) {
  return (
    <Stack alignItems="center" direction="row" justifyContent="space-between" sx={{ width: '100%' }}>
      <Typography color={option.available ? 'text.primary' : 'text.secondary'} variant="body2">
        {option.label}
      </Typography>
      {!option.available ? <Chip color="default" label="Coming soon" size="small" variant="outlined" /> : null}
    </Stack>
  );
}

function TripsWorkspace({ account, backgroundTaskManager, onRequestOrder }) {
  const appliedAiValidationTaskIdsRef = useRef(new Set());
  const requirementUploadInputRef = useRef(null);
  const cameraVideoRef = useRef(null);
  const cameraStreamRef = useRef(null);
  const [members, setMembers] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [wizardOpen, setWizardOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [form, setForm] = useState(initialForm);
  const [selectedTripId, setSelectedTripId] = useState('');
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [selectedTripTravelerId, setSelectedTripTravelerId] = useState('');
  const [tripLoading, setTripLoading] = useState(false);
  const [tripError, setTripError] = useState('');
  const [packageBusy, setPackageBusy] = useState(false);
  const [packageActionBusyKey, setPackageActionBusyKey] = useState('');
  const [expandedPackageIds, setExpandedPackageIds] = useState({});
  const [requirementDialogOpen, setRequirementDialogOpen] = useState(false);
  const [activeRequirement, setActiveRequirement] = useState(null);
  const [requirementUploadBusy, setRequirementUploadBusy] = useState(false);
  const [requirementLibraryDialogOpen, setRequirementLibraryDialogOpen] = useState(false);
  const [requirementLinkBusy, setRequirementLinkBusy] = useState(false);
  const [requirementLibraryError, setRequirementLibraryError] = useState('');
  const [requirementDialogError, setRequirementDialogError] = useState('');
  const [linkedDocumentDialogDocument, setLinkedDocumentDialogDocument] = useState(null);
  const [templateDownloadBusyKey, setTemplateDownloadBusyKey] = useState('');
  const [linkedDocumentActionTarget, setLinkedDocumentActionTarget] = useState(null);
  const [linkedDocumentActionType, setLinkedDocumentActionType] = useState('');
  const [linkedDocumentActionBusy, setLinkedDocumentActionBusy] = useState(false);
  const [linkedDocumentActionError, setLinkedDocumentActionError] = useState('');
  const [aiValidationConfirmTarget, setAiValidationConfirmTarget] = useState(null);
  const [aiValidationError, setAiValidationError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [cameraDialogOpen, setCameraDialogOpen] = useState(false);
  const [cameraBusy, setCameraBusy] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [cameraStream, setCameraStream] = useState(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraFacingMode, setCameraFacingMode] = useState('user');
  const [cameraCapturedFile, setCameraCapturedFile] = useState(null);
  const [cameraCapturedPreviewUrl, setCameraCapturedPreviewUrl] = useState('');
  const [cameraChoiceDialogOpen, setCameraChoiceDialogOpen] = useState(false);
  const [livePhotoDialogOpen, setLivePhotoDialogOpen] = useState(false);
  const [livePhotoBusy, setLivePhotoBusy] = useState(false);
  const [livePhotoError, setLivePhotoError] = useState('');
  const [livePhotoSession, setLivePhotoSession] = useState(null);
  const [livePhotoStatus, setLivePhotoStatus] = useState(null);
  const [livePhotoFinalizeBusy, setLivePhotoFinalizeBusy] = useState(false);

  const documentsByMemberId = useMemo(
    () =>
      documents.reduce((accumulator, document) => {
        accumulator[document.memberId] = accumulator[document.memberId] ?? [];
        accumulator[document.memberId].push(document);
        return accumulator;
      }, {}),
    [documents]
  );

  const selectedMemberIds = useMemo(
    () =>
      Object.entries(form.travelers)
        .filter(([, traveler]) => traveler.selected)
        .map(([memberId]) => memberId),
    [form.travelers]
  );

  const selectedMembers = useMemo(
    () => members.filter((member) => selectedMemberIds.includes(member.memberId)),
    [members, selectedMemberIds]
  );
  const selectedTripRequirements = useMemo(
    () =>
      (selectedTrip?.requirements ?? []).filter((requirement) => requirement.memberId === selectedTripTravelerId),
    [selectedTrip?.requirements, selectedTripTravelerId]
  );
  const selectedTripPackages = useMemo(
    () =>
      (selectedTrip?.packages ?? []).filter((packageItem) => packageItem.memberId === selectedTripTravelerId),
    [selectedTrip?.packages, selectedTripTravelerId]
  );
  const activeRequirementMember = useMemo(
    () => members.find((member) => member.memberId === activeRequirement?.memberId) ?? null,
    [activeRequirement?.memberId, members]
  );
  const activeRequirementMetadata = useMemo(
    () => activeRequirement?.metadata ?? {},
    [activeRequirement?.metadata]
  );
  const activeRequirementOnlineFormEntryPoint = activeRequirementMetadata.online_form_entry_point ?? '';
  const activeRequirementAppointmentEntryPoint = activeRequirementMetadata.appointment_url_entry_point ?? '';
  const activeRequirementPhotoSizeX = activeRequirementMetadata.size_x;
  const activeRequirementPhotoSizeY = activeRequirementMetadata.size_y;
  const activeRequirementPhotoAspectRatio = useMemo(
    () => getPhotoAspectRatio(activeRequirementMetadata),
    [activeRequirementMetadata]
  );
  const activeRequirementLatestAiValidation = useMemo(
    () => getLatestRequirementAiValidation(activeRequirement),
    [activeRequirement]
  );
  const aiValidationTasks = useMemo(
    () => backgroundTaskManager?.tasks ?? [],
    [backgroundTaskManager?.tasks]
  );
  const activeRequirementAiValidationTask = useMemo(
    () => getRequirementAiValidationTask(aiValidationTasks, selectedTrip?.tripId, activeRequirement),
    [activeRequirement, aiValidationTasks, selectedTrip?.tripId]
  );
  const activeRequirementAcceptedLanguages = Array.isArray(activeRequirementMetadata.required_languages)
    ? activeRequirementMetadata.required_languages
    : [];
  const activeRequirementTemplateDocuments = Array.isArray(activeRequirementMetadata.template_documents)
    ? activeRequirementMetadata.template_documents
    : [];
  const sortedActiveRequirementDocuments = useMemo(() => {
    const documents = Array.isArray(activeRequirement?.documents) ? [...activeRequirement.documents] : [];
    return documents.sort((left, right) => {
      const rightTimestamp = new Date(right.updatedAtUtc ?? right.createdAtUtc ?? 0).getTime();
      const leftTimestamp = new Date(left.updatedAtUtc ?? left.createdAtUtc ?? 0).getTime();
      return rightTimestamp - leftTimestamp;
    });
  }, [activeRequirement]);
  const activeRequirementPrintablePhoto = useMemo(
    () => sortedActiveRequirementDocuments.find(isImageDocument) ?? null,
    [sortedActiveRequirementDocuments]
  );
  const availableRequirementLibraryDocuments = useMemo(() => {
    if (!activeRequirement?.memberId) {
      return [];
    }

    const linkedDocumentIds = new Set((activeRequirement.documents ?? []).map((document) => document.documentId));
    return (documentsByMemberId[activeRequirement.memberId] ?? []).filter(
      (document) => !linkedDocumentIds.has(document.documentId)
    );
  }, [activeRequirement, documentsByMemberId]);
  const livePhotoMobileUrl = useMemo(() => {
    if (!livePhotoSession?.sessionId) {
      return '';
    }

    return `${window.location.origin}/live-photo?sessionId=${encodeURIComponent(livePhotoSession.sessionId)}`;
  }, [livePhotoSession?.sessionId]);

  const steps = useMemo(
    () => [
      {
        title: 'Trip details',
        description: 'Pick destination and tentative travel dates.',
        status: currentStep > 0 ? 'complete' : 'active',
      },
      {
        title: 'Travelers',
        description: 'Choose who is joining and assign a visa type.',
        status: currentStep > 1 ? 'complete' : currentStep === 1 ? 'active' : 'upcoming',
      },
      {
        title: 'Documents',
        description: 'Select library documents for each traveler. You can add more documents later.',
        status: currentStep === 2 ? 'active' : 'upcoming',
      },
    ],
    [currentStep]
  );

  useEffect(() => {
    let ignore = false;

    async function loadWorkspace() {
      if (!account?.token) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');

      try {
        const [membersResponse, documentsResponse, tripsResponse] = await Promise.all([
          listFamilyMembers({ token: account.token }),
          listDocuments({ token: account.token }),
          listTrips({ token: account.token }),
        ]);

        if (ignore) {
          return;
        }

        setMembers(membersResponse.members ?? []);
        setDocuments(documentsResponse.documents ?? []);
        setTrips(tripsResponse.trips ?? []);
      } catch (loadError) {
        if (!ignore) {
          setError(loadError.message);
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    loadWorkspace();
    return () => {
      ignore = true;
    };
  }, [account?.token]);

  useEffect(() => {
    let ignore = false;

    async function loadTrip() {
      if (!account?.token || !selectedTripId) {
        setSelectedTrip(null);
        setTripLoading(false);
        return;
      }

      setTripLoading(true);
      setTripError('');

      try {
        const response = await getTrip({ token: account.token, tripId: selectedTripId });
        if (ignore) {
          return;
        }

        const nextTrip = response.trip ?? null;
        setSelectedTrip(nextTrip);
        setSelectedTripTravelerId((current) => {
          if (current && nextTrip?.travelers?.some((traveler) => traveler.memberId === current)) {
            return current;
          }

          return nextTrip?.travelers?.[0]?.memberId ?? '';
        });
      } catch (loadError) {
        if (!ignore) {
          setTripError(loadError.message);
        }
      } finally {
        if (!ignore) {
          setTripLoading(false);
        }
      }
    }

    loadTrip();
    return () => {
      ignore = true;
    };
  }, [account?.token, selectedTripId]);

  useEffect(() => {
    if (!cameraDialogOpen || !cameraStream || !cameraVideoRef.current) {
      return undefined;
    }

    const videoElement = cameraVideoRef.current;
    return () => {
      videoElement.pause();
      if (videoElement.srcObject === cameraStream) {
        videoElement.srcObject = null;
      }
    };
  }, [cameraDialogOpen, cameraStream]);

  useEffect(() => {
    aiValidationTasks.forEach((task) => {
      if (task.status !== 'succeeded' || !task.requirement || appliedAiValidationTaskIdsRef.current.has(task.taskId)) {
        return;
      }

      applyUpdatedRequirement(task.requirement);
      appliedAiValidationTaskIdsRef.current.add(task.taskId);
    });
  }, [aiValidationTasks]);

  useEffect(() => () => {
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach((track) => track.stop());
    }
  }, []);

  useEffect(() => () => {
    if (cameraCapturedPreviewUrl) {
      URL.revokeObjectURL(cameraCapturedPreviewUrl);
    }
  }, [cameraCapturedPreviewUrl]);

  useEffect(() => {
    if (!livePhotoDialogOpen || !livePhotoSession?.sessionId) {
      return undefined;
    }

    let cancelled = false;

    async function refreshStatus() {
      try {
        const response = await getLivePhotoLinkStatus({ sessionId: livePhotoSession.sessionId });
        if (cancelled) {
          return;
        }

        setLivePhotoStatus(response);
        setLivePhotoError('');
      } catch (statusError) {
        if (!cancelled) {
          setLivePhotoError(statusError.message);
        }
      }
    }

    refreshStatus();
    const intervalId = window.setInterval(refreshStatus, 3000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [livePhotoDialogOpen, livePhotoSession?.sessionId]);

  function openWizard() {
    setWizardOpen(true);
    setSelectedTripId('');
    setSelectedTrip(null);
    setCurrentStep(0);
    setSuccessMessage('');
    setError('');
    setForm(initialForm);
  }

  function closeWizard() {
    setWizardOpen(false);
    setCurrentStep(0);
    setForm(initialForm);
  }

  function openTrip(tripId) {
    setWizardOpen(false);
    setSelectedTripId(tripId);
    setSelectedTrip(null);
    setTripError('');
    setExpandedPackageIds({});
    setRequirementDialogOpen(false);
    setActiveRequirement(null);
  }

  function closeTrip() {
    setSelectedTripId('');
    setSelectedTrip(null);
    setSelectedTripTravelerId('');
    setTripError('');
    setExpandedPackageIds({});
    setRequirementDialogOpen(false);
    setActiveRequirement(null);
  }

  function handleDeleteTrip(trip) {
    if (!account?.token || !trip) {
      return;
    }

    setDeleteTarget(trip);
    setDeleteError('');
  }

  async function handleConfirmDeleteTrip() {
    if (!account?.token || !deleteTarget) {
      return;
    }

    setDeleteBusy(true);
    setDeleteError('');

    try {
      await deleteTrip({
        token: account.token,
        tripId: deleteTarget.tripId,
      });

      setTrips((current) => current.filter((trip) => trip.tripId !== deleteTarget.tripId));

      if (selectedTripId === deleteTarget.tripId) {
        closeTrip();
      }

      setSuccessMessage('Trip deleted.');
      setDeleteTarget(null);
    } catch (deleteTripError) {
      setDeleteError(deleteTripError.message);
    } finally {
      setDeleteBusy(false);
    }
  }

  async function handleCreatePackage() {
    if (!account?.token || !selectedTrip?.tripId || !selectedTripTravelerId) {
      return;
    }

    setPackageBusy(true);
    setTripError('');
    setSuccessMessage('');

    try {
      const response = await createTripPackage({
        token: account.token,
        tripId: selectedTrip.tripId,
        memberId: selectedTripTravelerId,
      });

      applyUpsertedPackage(response.package);
      setSuccessMessage('Visa package created. Download it when you are ready.');
    } catch (packageError) {
      setTripError(packageError.message);
    } finally {
      setPackageBusy(false);
    }
  }

  async function handleDownloadPackage(packageItem) {
    if (!account?.token || !selectedTrip?.tripId || !selectedTripTravelerId || !packageItem) {
      return;
    }

    setPackageActionBusyKey(`download-${packageItem.packageId}`);
    setTripError('');
    setSuccessMessage('');

    try {
      const response = await getTripPackageDownloadUrl({
        token: account.token,
        tripId: selectedTrip.tripId,
        memberId: selectedTripTravelerId,
        packageId: packageItem.packageId,
      });

      applyUpsertedPackage({
        ...packageItem,
        packageDocumentId: response.documentId,
        updatedAtUtc: new Date().toISOString(),
      });
      downloadFileFromUrl(response.viewUrl, response.fileName ?? 'visa-package.zip');
    } catch (packageError) {
      setTripError(packageError.message);
    } finally {
      setPackageActionBusyKey('');
    }
  }

  async function handleDeletePackage(packageItem) {
    if (!account?.token || !selectedTrip?.tripId || !selectedTripTravelerId || !packageItem) {
      return;
    }

    setPackageActionBusyKey(`delete-${packageItem.packageId}`);
    setTripError('');
    setSuccessMessage('');

    try {
      await deleteTripPackage({
        token: account.token,
        tripId: selectedTrip.tripId,
        memberId: selectedTripTravelerId,
        packageId: packageItem.packageId,
      });

      applyDeletedPackage(packageItem.packageId);
      setSuccessMessage('Visa package deleted.');
    } catch (packageError) {
      setTripError(packageError.message);
    } finally {
      setPackageActionBusyKey('');
    }
  }

  function toggleTraveler(memberId, checked) {
    setForm((current) => {
      const nextDocuments = { ...current.documentSelections };
      if (checked && !nextDocuments[memberId]) {
        nextDocuments[memberId] = (documentsByMemberId[memberId] ?? []).map((document) => document.documentId);
      }

      if (!checked) {
        delete nextDocuments[memberId];
      }

      return {
        ...current,
        travelers: {
          ...current.travelers,
          [memberId]: {
            selected: checked,
            visaType: current.travelers[memberId]?.visaType ?? 'SchengenTourism',
          },
        },
        documentSelections: nextDocuments,
      };
    });
  }

  function updateTravelerVisaType(memberId, visaType) {
    setForm((current) => ({
      ...current,
      travelers: {
        ...current.travelers,
        [memberId]: {
          selected: current.travelers[memberId]?.selected ?? false,
          visaType,
        },
      },
    }));
  }

  function toggleDocument(memberId, documentId, checked) {
    setForm((current) => {
      const selectedDocumentIds = new Set(current.documentSelections[memberId] ?? []);
      if (checked) {
        selectedDocumentIds.add(documentId);
      } else {
        selectedDocumentIds.delete(documentId);
      }

      return {
        ...current,
        documentSelections: {
          ...current.documentSelections,
          [memberId]: Array.from(selectedDocumentIds),
        },
      };
    });
  }

  function validateStep(step) {
    if (step === 0) {
      if (!form.departureDate) {
        return 'Choose a departure date.';
      }

      if (form.returnDate && form.returnDate < form.departureDate) {
        return 'Return date cannot be earlier than departure date.';
      }
    }

    if (step === 1) {
      if (selectedMemberIds.length === 0) {
        return 'Select at least one family member.';
      }
    }

    return '';
  }

  function handleContinue() {
    const validationMessage = validateStep(currentStep);
    if (validationMessage) {
      setError(validationMessage);
      return;
    }

    setError('');
    setCurrentStep((value) => Math.min(value + 1, 2));
  }

  async function handleCreateTrip() {
    const validationMessage = validateStep(0) || validateStep(1);
    if (validationMessage) {
      setError(validationMessage);
      return;
    }

    if (!account?.token) {
      return;
    }

    setSaving(true);
    setError('');
    setSuccessMessage('');

    try {
      const createdTrip = await createTrip({
        token: account.token,
        trip: {
          destinationCountryCode: form.destinationCountryCode,
          departureDate: form.departureDate,
          returnDate: form.returnDate || null,
          travelers: selectedMemberIds.map((memberId) => ({
            memberId,
            visaType: form.travelers[memberId]?.visaType ?? 'SchengenTourism',
            documentIds: form.documentSelections[memberId] ?? [],
          })),
        },
      });

      setTrips((current) => [createdTrip, ...current]);
      setSuccessMessage('Trip created.');
      closeWizard();
      setSelectedTripId(createdTrip.tripId);
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setSaving(false);
    }
  }

  function openRequirementDialog(requirement) {
    setActiveRequirement(requirement);
    setRequirementDialogError('');
    setRequirementLibraryError('');
    setRequirementDialogOpen(true);
  }

  function closeRequirementDialog() {
    if (
      requirementUploadBusy ||
      requirementLinkBusy ||
      linkedDocumentActionBusy ||
      cameraBusy ||
      livePhotoBusy ||
      livePhotoFinalizeBusy
    ) {
      return;
    }

    setRequirementDialogOpen(false);
    setRequirementLibraryDialogOpen(false);
    setActiveRequirement(null);
    setRequirementDialogError('');
    setRequirementLibraryError('');
    setLinkedDocumentDialogDocument(null);
    setTemplateDownloadBusyKey('');
    closeCameraChoiceDialog();
    closeLivePhotoDialog();
    closeCameraDialog();
  }

  function openRequirementLibraryDialog() {
    if (activeRequirement?.type === 'Photo') {
      return;
    }

    setRequirementLibraryError('');
    setRequirementDialogError('');
    setRequirementLibraryDialogOpen(true);
  }

  function closeRequirementLibraryDialog() {
    if (requirementLinkBusy) {
      return;
    }

    setRequirementLibraryDialogOpen(false);
    setRequirementLibraryError('');
  }

  function cleanupCameraStream(streamToStop = cameraStream) {
    if (streamToStop) {
      streamToStop.getTracks().forEach((track) => track.stop());
    }
  }

  function resetCapturedCameraPhoto() {
    if (cameraCapturedPreviewUrl) {
      URL.revokeObjectURL(cameraCapturedPreviewUrl);
    }

    setCameraCapturedFile(null);
    setCameraCapturedPreviewUrl('');
  }

  async function attachCameraStream(stream, attemptsRemaining = 20) {
    const videoElement = cameraVideoRef.current;

    if (!videoElement) {
      if (attemptsRemaining <= 0) {
        throw new Error('The camera preview could not be prepared. Please try again.');
      }

      await new Promise((resolve) => window.setTimeout(resolve, 100));
      return attachCameraStream(stream, attemptsRemaining - 1);
    }

    videoElement.srcObject = stream;
    videoElement.muted = true;
    videoElement.playsInline = true;
    videoElement.autoplay = true;

    try {
      await videoElement.play();
    } catch {
      // Some browsers only succeed after metadata/canplay becomes available.
    }

    if (videoElement.videoWidth > 0 && videoElement.videoHeight > 0) {
      setCameraReady(true);
      return;
    }

    if (attemptsRemaining <= 0) {
      throw new Error('The live camera preview did not start. Please close this dialog and try again.');
    }

    await new Promise((resolve) => window.setTimeout(resolve, 150));

    try {
      await videoElement.play();
    } catch {
      // Retry loop below handles delayed readiness.
    }

    return attachCameraStream(stream, attemptsRemaining - 1);
  }

  async function startCameraStream(facingMode = cameraFacingMode) {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error('Camera capture is not supported in this browser.');
    }

    setCameraError('');
    setCameraReady(false);
    cleanupCameraStream(cameraStreamRef.current);
    cameraStreamRef.current = null;
    setCameraStream(null);

    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode,
        width: { ideal: 1600 },
        height: { ideal: 1600 },
      },
      audio: false,
    });

    cleanupCameraStream(stream === cameraStreamRef.current ? null : cameraStreamRef.current);
    cameraStreamRef.current = stream;
    setCameraStream(stream);
    await new Promise((resolve) => window.setTimeout(resolve, 50));
    await attachCameraStream(stream);
  }

  async function handleFlipCamera() {
    if (cameraBusy) {
      return;
    }

    const nextFacingMode = cameraFacingMode === 'user' ? 'environment' : 'user';
    const previousFacingMode = cameraFacingMode;
    setCameraFacingMode(nextFacingMode);

    try {
      await startCameraStream(nextFacingMode);
    } catch (streamError) {
      setCameraFacingMode(previousFacingMode);
      try {
        await startCameraStream(previousFacingMode);
      } catch {
        cleanupCameraStream(cameraStreamRef.current);
        cameraStreamRef.current = null;
        setCameraStream(null);
        setCameraReady(false);
      }
      setCameraError(streamError?.message ?? 'We could not switch cameras. Please try again.');
    }
  }

  async function handleTakePhotoClick() {
    if (isProbablyMobileDevice()) {
      await openCameraDialog();
      return;
    }

    setCameraChoiceDialogOpen(true);
    setLivePhotoError('');
    setRequirementDialogError('');
  }

  function closeCameraChoiceDialog() {
    if (livePhotoBusy || livePhotoFinalizeBusy) {
      return;
    }

    setCameraChoiceDialogOpen(false);
  }

  async function handleChooseDesktopCamera() {
    setCameraChoiceDialogOpen(false);
    await openCameraDialog();
  }

  function closeLivePhotoDialog() {
    if (livePhotoBusy || livePhotoFinalizeBusy) {
      return;
    }

    setLivePhotoDialogOpen(false);
    setLivePhotoError('');
    setLivePhotoStatus(null);
    setLivePhotoSession(null);
  }

  async function handleChoosePhoneCamera() {
    if (!account?.token || !selectedTrip || !activeRequirement) {
      return;
    }

    setCameraChoiceDialogOpen(false);
    setLivePhotoDialogOpen(true);
    setLivePhotoBusy(true);
    setLivePhotoError('');
    setLivePhotoStatus(null);
    setRequirementDialogError('');

    try {
      const session = await createLivePhotoLink({
        token: account.token,
        tripId: selectedTrip.tripId,
        memberId: activeRequirement.memberId,
        requirementUuid: activeRequirement.requirementUuid,
      });

      setLivePhotoSession(session);
    } catch (sessionError) {
      setLivePhotoError(sessionError.message);
    } finally {
      setLivePhotoBusy(false);
    }
  }

  async function handleRefreshLivePhotoSession() {
    await handleChoosePhoneCamera();
  }

  async function openCameraDialog() {
    if (!navigator.mediaDevices?.getUserMedia) {
      setRequirementDialogError('Camera capture is not supported in this browser.');
      return;
    }

    setCameraError('');
    setRequirementDialogError('');
    setCameraReady(false);
    setCameraFacingMode('user');
    resetCapturedCameraPhoto();
    setCameraDialogOpen(true);

    try {
      await startCameraStream('user');
    } catch (streamError) {
      setCameraDialogOpen(false);
      cleanupCameraStream(cameraStreamRef.current);
      cameraStreamRef.current = null;
      setCameraStream(null);
      setCameraReady(false);
      setRequirementDialogError(streamError?.message || 'We could not access the camera. Please allow camera access and try again.');
    }
  }

  function closeCameraDialog(force = false) {
    if (cameraBusy && !force) {
      return;
    }

    cleanupCameraStream(cameraStreamRef.current);
    cameraStreamRef.current = null;
    setCameraStream(null);
    setCameraDialogOpen(false);
    setCameraError('');
    setCameraReady(false);
    setCameraFacingMode('user');
    resetCapturedCameraPhoto();
  }

  async function handleRetryCapturedPhoto() {
    setCameraError('');
    setCameraReady(false);
    resetCapturedCameraPhoto();

    try {
      await startCameraStream(cameraFacingMode);
    } catch (streamError) {
      cleanupCameraStream(cameraStreamRef.current);
      cameraStreamRef.current = null;
      setCameraStream(null);
      setCameraReady(false);
      setCameraError(streamError?.message ?? 'We could not restart the camera preview. Please try again.');
    }
  }

  async function handleFinalizeLivePhoto() {
    if (!account?.token || !livePhotoSession?.sessionId) {
      return;
    }

    setLivePhotoFinalizeBusy(true);
    setLivePhotoError('');

    try {
      const response = await finalizeLivePhotoLink({
        token: account.token,
        sessionId: livePhotoSession.sessionId,
      });

      setDocuments((current) => {
        const remainingDocuments = current.filter((document) => document.documentId !== response.document.documentId);
        return [response.document, ...remainingDocuments];
      });
      applyUpdatedRequirement(response.requirement);
      setSuccessMessage('Photo received from your phone and linked to this requirement.');
      setLivePhotoDialogOpen(false);
      setLivePhotoStatus(null);
      setLivePhotoSession(null);
    } catch (finalizeError) {
      setLivePhotoError(finalizeError.message);
    } finally {
      setLivePhotoFinalizeBusy(false);
    }
  }

  function applyUpdatedRequirement(updatedRequirement) {
    setSelectedTrip((current) =>
      current
        ? {
          ...current,
          requirements: current.requirements.map((requirement) =>
            requirement.memberId === updatedRequirement.memberId &&
              requirement.requirementUuid === updatedRequirement.requirementUuid
              ? updatedRequirement
              : requirement
          ),
        }
        : current
    );
    setActiveRequirement((current) =>
      current &&
        current.memberId === updatedRequirement.memberId &&
        current.requirementUuid === updatedRequirement.requirementUuid
        ? updatedRequirement
        : current
    );
  }

  function applyUpsertedPackage(updatedPackage) {
    setSelectedTrip((current) =>
      current
        ? {
          ...current,
          packages: [
            updatedPackage,
            ...(current.packages ?? []).filter((packageItem) => packageItem.packageId !== updatedPackage.packageId),
          ].sort((left, right) => new Date(right.createdAtUtc).getTime() - new Date(left.createdAtUtc).getTime()),
        }
        : current
    );
  }

  function applyDeletedPackage(packageId) {
    setSelectedTrip((current) =>
      current
        ? {
          ...current,
          packages: (current.packages ?? []).filter((packageItem) => packageItem.packageId !== packageId),
        }
        : current
    );
    setExpandedPackageIds((current) => {
      if (!current[packageId]) {
        return current;
      }

      const nextExpanded = { ...current };
      delete nextExpanded[packageId];
      return nextExpanded;
    });
  }

  function togglePackageDocuments(packageId) {
    setExpandedPackageIds((current) => ({
      ...current,
      [packageId]: !current[packageId],
    }));
  }

  function openAiValidationConfirmDialog(requirement) {
    setAiValidationConfirmTarget(requirement);
    setAiValidationError('');
  }

  function closeAiValidationConfirmDialog() {
    setAiValidationConfirmTarget(null);
    setAiValidationError('');
  }

  async function handleConfirmAiValidation() {
    if (!account?.token || !selectedTrip || !aiValidationConfirmTarget || !backgroundTaskManager?.upsertTask) {
      return;
    }

    setAiValidationError('');
    setRequirementDialogError('');

    try {
      const task = await startTripRequirementAiValidation({
          token: account.token,
          tripId: selectedTrip.tripId,
          memberId: aiValidationConfirmTarget.memberId,
          requirementUuid: aiValidationConfirmTarget.requirementUuid,
        });

      backgroundTaskManager.upsertTask(task);
      backgroundTaskManager.openTray?.();
      setSuccessMessage('AI validation is running in the background.');
      setAiValidationConfirmTarget(null);
    } catch (validationError) {
      setAiValidationError(validationError.message);
    }
  }

  function openLinkedDocumentDialog(document) {
    setLinkedDocumentDialogDocument(document);
  }

  function closeLinkedDocumentDialog() {
    setLinkedDocumentDialogDocument(null);
  }

  function handleLinkedDocumentSaved(updatedDocument) {
    setDocuments((current) =>
      current.map((document) => (document.documentId === updatedDocument.documentId ? updatedDocument : document))
    );

    setSelectedTrip((current) =>
      current
        ? {
          ...current,
          requirements: current.requirements.map((requirement) => ({
            ...requirement,
            documents: (requirement.documents ?? []).map((document) =>
              document.documentId === updatedDocument.documentId ? updatedDocument : document
            ),
          })),
        }
        : current
    );

    setActiveRequirement((current) =>
      current
        ? {
          ...current,
          documents: (current.documents ?? []).map((document) =>
            document.documentId === updatedDocument.documentId ? updatedDocument : document
          ),
        }
        : current
    );

    setLinkedDocumentDialogDocument(updatedDocument);
  }

  function openLinkedDocumentAction(actionType, document) {
    setLinkedDocumentActionType(actionType);
    setLinkedDocumentActionTarget(document);
    setLinkedDocumentActionError('');
  }

  function closeLinkedDocumentActionDialog() {
    if (linkedDocumentActionBusy) {
      return;
    }

    setLinkedDocumentActionTarget(null);
    setLinkedDocumentActionType('');
    setLinkedDocumentActionError('');
  }

  function handleRequestTranslationOrder(event, document) {
    event.stopPropagation();
    event.preventDefault();

    if (activeRequirement?.type !== 'Document') {
      return;
    }

    onRequestOrder?.({
      type: 'translation',
      tripId: selectedTrip?.tripId ?? null,
      sourceRequirementUuid: activeRequirement?.requirementUuid ?? null,
      sourceRequirementTitle: activeRequirement?.title ?? '',
      metadata: {
        availableTargetLanguages: activeRequirementAcceptedLanguages,
      },
      documents: [
        {
          memberId: document.memberId,
          documentId: document.documentId,
        },
      ],
    });
  }

  function handleRequestVisaAppointmentOrder(event) {
    event.stopPropagation();
    event.preventDefault();

    if (!selectedTrip || !activeRequirement) {
      return;
    }

    onRequestOrder?.({
      type: 'visa_appointment',
      tripId: selectedTrip.tripId,
      sourceRequirementUuid: activeRequirement.requirementUuid,
      sourceRequirementTitle: activeRequirement.title ?? 'Visa appointment',
      documents: [],
    });
  }

  function handleRequestPrintPackageOrder(event, packageItem) {
    event.stopPropagation();
    event.preventDefault();

    if (!selectedTrip || !packageItem) {
      return;
    }

    onRequestOrder?.({
      type: 'print_package',
      tripId: selectedTrip.tripId,
      sourceRequirementUuid: null,
      sourceRequirementTitle: `Printed visa package for ${selectedTrip.destinationCountryName}`,
      metadata: {
        tripId: selectedTrip.tripId,
        memberId: packageItem.memberId,
        packageId: packageItem.packageId,
        packageLabel: `${selectedTrip.destinationCountryName} • ${formatDateTime(packageItem.createdAtUtc)}`,
      },
      documents: [],
    });
  }

  function handleRequestPrintPhotosOrder(event, document = activeRequirementPrintablePhoto) {
    event.stopPropagation();
    event.preventDefault();

    if (!selectedTrip || !activeRequirement || !document || activeRequirement.type !== 'Photo') {
      return;
    }

    onRequestOrder?.({
      type: 'print_photos',
      tripId: selectedTrip.tripId,
      sourceRequirementUuid: activeRequirement.requirementUuid,
      sourceRequirementTitle: activeRequirement.title ?? 'Photo printing',
      metadata: {
        photoCount: 8,
        photoLabel: document.fileName,
      },
      documents: [
        {
          memberId: document.memberId,
          documentId: document.documentId,
        },
      ],
    });
  }

  async function handleConfirmLinkedDocumentAction() {
    if (!account?.token || !selectedTrip || !activeRequirement || !linkedDocumentActionTarget || !linkedDocumentActionType) {
      return;
    }

    setLinkedDocumentActionBusy(true);
    setLinkedDocumentActionError('');
    setRequirementDialogError('');

    try {
      if (linkedDocumentActionType === 'unlink') {
        const updatedRequirement = await removeTripRequirementDocument({
          token: account.token,
          tripId: selectedTrip.tripId,
          memberId: activeRequirement.memberId,
          requirementUuid: activeRequirement.requirementUuid,
          documentId: linkedDocumentActionTarget.documentId,
        });

        applyUpdatedRequirement(updatedRequirement);
        if (linkedDocumentDialogDocument?.documentId === linkedDocumentActionTarget.documentId) {
          closeLinkedDocumentDialog();
        }
        setSuccessMessage('Document unlinked from this requirement.');
      } else if (linkedDocumentActionType === 'delete') {
        await deleteDocument({
          token: account.token,
          memberId: linkedDocumentActionTarget.memberId,
          documentId: linkedDocumentActionTarget.documentId,
        });

        setDocuments((current) =>
          current.filter((document) => document.documentId !== linkedDocumentActionTarget.documentId)
        );
        setSelectedTrip((current) =>
          current
            ? {
              ...current,
              requirements: current.requirements.map((requirement) => ({
                ...requirement,
                documents: (requirement.documents ?? []).filter(
                  (document) => document.documentId !== linkedDocumentActionTarget.documentId
                ),
              })),
            }
            : current
        );
        setActiveRequirement((current) =>
          current
            ? {
              ...current,
              documents: (current.documents ?? []).filter(
                (document) => document.documentId !== linkedDocumentActionTarget.documentId
              ),
            }
            : current
        );
        if (linkedDocumentDialogDocument?.documentId === linkedDocumentActionTarget.documentId) {
          closeLinkedDocumentDialog();
        }
        setSuccessMessage('Document deleted.');
      }

      setLinkedDocumentActionTarget(null);
      setLinkedDocumentActionType('');
      setLinkedDocumentActionError('');
    } catch (actionError) {
      setLinkedDocumentActionError(actionError.message);
    } finally {
      setLinkedDocumentActionBusy(false);
    }
  }

  async function handleRequirementFilePicked(event) {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    if (activeRequirement?.type === 'Photo') {
      setRequirementDialogError('Photos can only be added by taking a photo.');
      return;
    }

    await uploadRequirementDocument(file);
  }

  async function uploadRequirementDocument(file, options = {}) {
    if (!file || !account?.token || !selectedTrip || !activeRequirement) {
      return;
    }

    setRequirementUploadBusy(true);
    if (!options.suppressDialogError) {
      setRequirementDialogError('');
    }

    let createdDocument = null;

    try {
      const upload = await createDocumentUpload({
        token: account.token,
        memberId: activeRequirement.memberId,
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
      });

      await uploadFileToPresignedUrl({
        uploadUrl: upload.uploadUrl,
        file,
        contentType: file.type || 'application/octet-stream',
      });

      createdDocument = await createDocument({
        token: account.token,
        memberId: activeRequirement.memberId,
        documentId: upload.documentId,
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
      });

      const updatedRequirement = await attachTripRequirementDocument({
        token: account.token,
        tripId: selectedTrip.tripId,
        memberId: activeRequirement.memberId,
        requirementUuid: activeRequirement.requirementUuid,
        documentId: createdDocument.documentId,
      });

      setDocuments((current) => [createdDocument, ...current]);
      applyUpdatedRequirement(updatedRequirement);
      return updatedRequirement;
    } catch (uploadError) {
      if (createdDocument?.documentId) {
        deleteDocument({
          token: account.token,
          memberId: createdDocument.memberId,
          documentId: createdDocument.documentId,
        }).catch(() => {
          // The primary failure should be surfaced to the user immediately; cleanup can be retried later if needed.
        });
      }
      if (!options.suppressDialogError) {
        setRequirementDialogError(uploadError.message);
      }

      throw uploadError;
    } finally {
      setRequirementUploadBusy(false);
    }
  }

  async function handleLinkExistingRequirementDocument(document) {
    if (!account?.token || !selectedTrip || !activeRequirement || !document?.documentId) {
      return;
    }

    setRequirementLinkBusy(true);
    setRequirementLibraryError('');
    setRequirementDialogError('');

    try {
      const updatedRequirement = await attachTripRequirementDocument({
        token: account.token,
        tripId: selectedTrip.tripId,
        memberId: activeRequirement.memberId,
        requirementUuid: activeRequirement.requirementUuid,
        documentId: document.documentId,
      });

      applyUpdatedRequirement(updatedRequirement);
      setSuccessMessage('Document linked from your library.');
      setRequirementLibraryDialogOpen(false);
    } catch (linkError) {
      setRequirementLibraryError(linkError.message);
    } finally {
      setRequirementLinkBusy(false);
    }
  }

  async function handleCapturePhoto() {
    if (!cameraVideoRef.current) {
      setCameraError('Camera preview is not ready yet.');
      return;
    }

    const video = cameraVideoRef.current;
    const videoWidth = video.videoWidth;
    const videoHeight = video.videoHeight;

    if (!videoWidth || !videoHeight) {
      setCameraError('Camera preview is not ready yet. Try again in a moment.');
      return;
    }

    setCameraError('');

    try {
      const targetAspectRatio = activeRequirementPhotoAspectRatio;
      let cropWidth = videoWidth;
      let cropHeight = Math.round(cropWidth / targetAspectRatio);

      if (cropHeight > videoHeight) {
        cropHeight = videoHeight;
        cropWidth = Math.round(cropHeight * targetAspectRatio);
      }

      const offsetX = Math.max(0, Math.round((videoWidth - cropWidth) / 2));
      const offsetY = Math.max(0, Math.round((videoHeight - cropHeight) / 2));

      const canvas = document.createElement('canvas');
      canvas.width = cropWidth;
      canvas.height = cropHeight;

      const context = canvas.getContext('2d');
      if (!context) {
        throw new Error('Unable to prepare the captured photo.');
      }

      context.drawImage(
        video,
        offsetX,
        offsetY,
        cropWidth,
        cropHeight,
        0,
        0,
        cropWidth,
        cropHeight
      );

      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (value) => {
            if (value) {
              resolve(value);
              return;
            }

            reject(new Error('Unable to capture a photo from the camera.'));
          },
          'image/jpeg',
          0.95
        );
      });

      const capturedFile = new File(
        [blob],
        `visa-photo-${new Date().toISOString().replace(/[:.]/g, '-')}.jpg`,
        { type: 'image/jpeg' }
      );

      const previewUrl = URL.createObjectURL(capturedFile);

      cleanupCameraStream(cameraStreamRef.current);
      cameraStreamRef.current = null;
      setCameraStream(null);
      setCameraReady(false);
      resetCapturedCameraPhoto();
      setCameraCapturedFile(capturedFile);
      setCameraCapturedPreviewUrl(previewUrl);
    } catch (captureError) {
      setCameraError(captureError.message ?? 'Unable to capture a photo right now.');
    }
  }

  async function handleProceedCapturedPhoto() {
    if (!cameraCapturedFile) {
      setCameraError('Capture a photo first.');
      return;
    }

    setCameraBusy(true);
    setCameraError('');

    try {
      await uploadRequirementDocument(cameraCapturedFile, { suppressDialogError: true });
      closeCameraDialog(true);
    } catch (uploadError) {
      setCameraError(uploadError.message ?? 'Unable to validate this photo right now.');
    } finally {
      setCameraBusy(false);
    }
  }

  async function handleTemplateDownload(templateDocument) {
    if (!account?.token || !selectedTrip || !activeRequirement || !templateDocument?.key) {
      return;
    }

    setTemplateDownloadBusyKey(templateDocument.key);
    setRequirementDialogError('');

    try {
      const response = await getTripRequirementTemplateDownloadUrl({
        token: account.token,
        tripId: selectedTrip.tripId,
        memberId: activeRequirement.memberId,
        requirementUuid: activeRequirement.requirementUuid,
        templateKey: templateDocument.key,
      });

      downloadFileFromUrl(response.viewUrl, response.fileName ?? templateDocument.name ?? templateDocument.key);
    } catch (downloadError) {
      setRequirementDialogError(downloadError.message);
    } finally {
      setTemplateDownloadBusyKey('');
    }
  }

  return (
    <Stack spacing={3}>
      {activeRequirement?.type !== 'Photo' ? (
        <input hidden onChange={handleRequirementFilePicked} ref={requirementUploadInputRef} type="file" />
      ) : null}

      {error ? <Alert severity="error">{error}</Alert> : null}
      {successMessage ? <Alert severity="success">{successMessage}</Alert> : null}

      {!wizardOpen && !selectedTripId ? (
        <>
          {loading ? (
            <Typography color="text.secondary" variant="body2">
              Loading your trip workspace...
            </Typography>
          ) : members.length === 0 ? (
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'center',
                minHeight: { xs: 320, md: 420 },
                width: '100%',
              }}
            >
              <Paper
                sx={{
                  maxWidth: 520,
                  px: { xs: 2.5, sm: 4 },
                  py: { xs: 3, sm: 4 },
                  textAlign: 'center',
                  width: '100%',
                }}
                variant="outlined"
              >
                <Stack alignItems="center" spacing={1.5}>
                  <Typography sx={{ fontWeight: 700 }} variant="h6">
                    Add a family member first
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    You need to have at least one family member before creating a trip. Add your first one{' '}
                    <Link component={RouterLink} to={PORTAL_ROUTES.family} underline="hover">
                      here
                    </Link>
                    .
                  </Typography>
                </Stack>
              </Paper>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gap: 1.75,
                gridTemplateColumns: {
                  xs: '1fr',
                  md: 'repeat(2, minmax(0, 1fr))',
                  xl: 'repeat(3, minmax(0, 1fr))',
                },
              }}
            >
              {trips.map((trip) => (
                <TripCard key={trip.tripId} members={members} onDelete={handleDeleteTrip} onClick={openTrip} trip={trip} />
              ))}
              {members.length > 0 ? <AddTripTile onClick={openWizard} /> : null}
            </Box>
          )}
        </>
      ) : selectedTripId ? (
        <Stack spacing={3}>
          {tripError ? <Alert severity="error">{tripError}</Alert> : null}

          {tripLoading ? (
            <Paper sx={{ p: 3 }} variant="outlined">
              <Stack alignItems="center" spacing={1.5}>
                <CircularProgress size={24} />
                <Typography color="text.secondary" variant="body2">
                  Loading trip requirements...
                </Typography>
              </Stack>
            </Paper>
          ) : selectedTrip ? (
            <Box>
              <Box
                sx={{
                  alignItems: { xs: 'flex-start', lg: 'center' },
                  display: 'flex',
                  flexDirection: { xs: 'column', lg: 'row' },
                  gap: 2,
                  width: '100%',
                }}
              >
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={{ xs: 1.5, sm: 4 }}
                  sx={{ flex: 1, minWidth: 0 }}
                >
                  <Box>
                    <Typography color="text.secondary" variant="caption">
                      Trip Country
                    </Typography>
                    <Typography sx={{ fontWeight: 700 }} variant="subtitle1">
                      {selectedTrip.destinationCountryName}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography color="text.secondary" variant="caption">
                      Trip Date
                    </Typography>
                    <Typography sx={{ fontWeight: 700 }} variant="subtitle1">
                      {formatTripDateRange(selectedTrip)}
                    </Typography>
                  </Box>
                </Stack>

                <Button
                  onClick={closeTrip}
                  startIcon={<ArrowBackOutlined />}
                  sx={{ alignSelf: { xs: 'flex-start', lg: 'center' }, ml: { lg: 'auto' } }}
                  variant="text"
                >
                  Back to trips
                </Button>
              </Box>

              {selectedTrip?.travelers?.length ? (
                <TextField
                  label="Traveler"
                  onChange={(event) => setSelectedTripTravelerId(event.target.value)}
                  select
                  size="small"
                  sx={{ display: 'flex', mb: 2, ml: 'auto', mt: 2, maxWidth: 360, width: '100%' }}
                  value={selectedTripTravelerId}
                >
                  {selectedTrip.travelers.map((traveler) => {
                    const member = members.find((item) => item.memberId === traveler.memberId);
                    return (
                      <MenuItem key={traveler.memberId} value={traveler.memberId}>
                        {formatMemberName(member ?? {}) || 'Traveler'} ({formatVisaType(traveler.visaType)})
                      </MenuItem>
                    );
                  })}
                </TextField>
              ) : null}

              <Accordion disableGutters sx={{ mb: 2, overflow: 'hidden' }} variant="outlined">
                <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
                  <Box
                    sx={{
                      display: 'grid',
                      gap: 1,
                      gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) auto' },
                      width: '100%',
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700 }} variant="subtitle1">
                        My Visa Packages
                      </Typography>
                      <Typography color="text.secondary" variant="body2">
                        Get a ready to download, and ready to print visa package.
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        alignItems: { xs: 'flex-start', sm: 'flex-start' },
                        display: 'flex',
                        justifyContent: { sm: 'flex-end' },
                        minWidth: 'fit-content',
                      }}
                    >
                      <Button
                        disabled={packageBusy || !selectedTripTravelerId}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          handleCreatePackage();
                        }}
                        startIcon={<AddOutlined />}
                        size="small"
                        sx={{
                          color: 'text.secondary',
                          justifyContent: 'flex-end',
                          minWidth: 'fit-content',
                          px: 0.5,
                        }}
                        variant="text"
                      >
                        {packageBusy ? 'Generating...' : 'Generate new package'}
                      </Button>
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Stack spacing={2}>

                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Timestamp</TableCell>
                            <TableCell>Documents</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedTripPackages.length > 0 ? (
                            selectedTripPackages.map((packageItem) => {
                              const expanded = Boolean(expandedPackageIds[packageItem.packageId]);
                              const busyAction = packageActionBusyKey;
                              const isDownloading = busyAction === `download-${packageItem.packageId}`;

                              return (
                                <TableRow key={packageItem.packageId}>
                                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                    {formatDateTime(packageItem.createdAtUtc)}
                                  </TableCell>
                                  <TableCell sx={{ minWidth: 320 }}>
                                    <Stack spacing={0.75}>
                                      <Typography variant="body2">
                                        {expanded
                                          ? formatPackageDocumentsSummary(packageItem)
                                          : truncatePackageDocuments(packageItem)}
                                      </Typography>
                                      {(packageItem.sourceDocumentNames ?? []).length > 2 ? (
                                        <Button
                                          onClick={() => togglePackageDocuments(packageItem.packageId)}
                                          size="small"
                                          sx={{ alignSelf: 'flex-start', px: 0 }}
                                          variant="text"
                                        >
                                          {expanded ? 'Show less' : 'Show all'}
                                        </Button>
                                      ) : null}
                                      <Collapse in={expanded} timeout="auto" unmountOnExit>
                                        <Typography color="text.secondary" variant="caption">
                                          {(packageItem.sourceDocumentIds ?? []).length} document
                                          {(packageItem.sourceDocumentIds ?? []).length === 1 ? '' : 's'} captured
                                        </Typography>
                                      </Collapse>
                                    </Stack>
                                  </TableCell>
                                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                    <Stack direction="row" justifyContent="flex-end" spacing={1}>
                                      <Button
                                        disabled={Boolean(packageActionBusyKey)}
                                        onClick={(event) => handleRequestPrintPackageOrder(event, packageItem)}
                                        size="small"
                                        variant="text"
                                      >
                                        Print package
                                      </Button>
                                      <Button
                                        disabled={Boolean(packageActionBusyKey)}
                                        onClick={() => handleDownloadPackage(packageItem)}
                                        size="small"
                                        startIcon={<DownloadOutlined />}
                                        variant="outlined"
                                      >
                                        {isDownloading ? 'Preparing...' : 'Download package'}
                                      </Button>
                                      <IconButton
                                        aria-label="delete visa package"
                                        disabled={Boolean(packageActionBusyKey)}
                                        onClick={() => handleDeletePackage(packageItem)}
                                        size="small"
                                      >
                                        <DeleteOutlined fontSize="small" />
                                      </IconButton>
                                    </Stack>
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          ) : (
                            <TableRow>
                              <TableCell colSpan={3}>
                                <Typography color="text.secondary" variant="body2">
                                  No visa packages yet for this traveler.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Stack>
                </AccordionDetails>
              </Accordion>

              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Requirement</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>AI Validation</TableCell>
                      <TableCell>Documents</TableCell>
                      <TableCell>Updated</TableCell>
                      <TableCell>Official link</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedTripRequirements.length > 0 ? (
                      selectedTripRequirements.map((requirement) => (
                        <TableRow
                          hover
                          key={`${requirement.memberId}-${requirement.requirementUuid}`}
                          onClick={() => openRequirementDialog(requirement)}
                          sx={{
                            cursor: 'pointer',
                            '&:last-child td, &:last-child th': { borderBottom: 0 },
                          }}
                        >
                          <TableCell sx={{ minWidth: 260 }}>
                            <Stack alignItems="center" direction="row" spacing={1.5}>
                              <Box
                                sx={{
                                  alignItems: 'center',
                                  bgcolor: 'background.default',
                                  borderRadius: 1.5,
                                  color: 'text.secondary',
                                  display: 'flex',
                                  height: 36,
                                  justifyContent: 'center',
                                  width: 36,
                                }}
                              >
                                <DescriptionOutlined fontSize="small" />
                              </Box>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontWeight: 600 }}>{requirement.title}</Typography>
                              </Box>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Chip label={formatRequirementType(requirement.type)} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            {requirementSupportsAiValidation(requirement) ? (
                              getRequirementAiValidationTask(aiValidationTasks, selectedTrip?.tripId, requirement) ? (
                                <Chip
                                  color={getRequirementAiValidationStatusColor(requirement, true)}
                                  label={getRequirementAiValidationStatusLabel(requirement, true)}
                                  size="small"
                                  variant="filled"
                                />
                              ) : getLatestRequirementAiValidation(requirement) ? (
                                <Tooltip title={getRequirementAiValidationRationale(requirement) || 'No rationale available'}>
                                  <Chip
                                    color={getRequirementAiValidationStatusColor(requirement)}
                                    label={getRequirementAiValidationStatusLabel(requirement)}
                                    size="small"
                                    variant="filled"
                                  />
                                </Tooltip>
                              ) : (
                                <Chip
                                  clickable
                                  color="warning"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    openAiValidationConfirmDialog(requirement);
                                  }}
                                  size="small"
                                  label="Pending AI Validation"
                                  variant="filled"
                                />
                              )
                            ) : (
                              <Chip
                                color="default"
                                label="Not applicable"
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              color="default"
                              label={`${requirement.documents.length} linked`}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>{formatDateTime(requirement.updatedAtUtc)}</TableCell>
                          <TableCell>
                            {requirement.officialLink ? (
                              <Button
                                href={requirement.officialLink}
                                onClick={(event) => event.stopPropagation()}
                                rel="noreferrer"
                                size="small"
                                startIcon={<OpenInNewOutlined />}
                                target="_blank"
                                variant="text"
                              >
                                Open
                              </Button>
                            ) : (
                              <Typography color="text.secondary" variant="body2">
                                Not available
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6}>
                          <Stack spacing={1}>
                            <Typography variant="subtitle1">No requirements loaded</Typography>
                            <Typography color="text.secondary" variant="body2">
                              This traveler does not have requirement definitions in the country requirements catalog yet.
                            </Typography>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : null}
        </Stack>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gap: 3,
            gridTemplateColumns: {
              xs: '1fr',
              xl: 'minmax(280px, 0.75fr) minmax(0, 1.55fr)',
            },
          }}
        >
          <SectionCard subtitle="Let's Go!" title="Create trip">
            <JourneyStepper steps={steps} />
          </SectionCard>

          <SectionCard subtitle={steps[currentStep].description} title={steps[currentStep].title}>
            {currentStep === 0 ? (
              <Stack spacing={2.5}>
                <TextField
                  fullWidth
                  label="Destination country"
                  onChange={(event) =>
                    setForm((current) => ({ ...current, destinationCountryCode: event.target.value }))
                  }
                  select
                  value={form.destinationCountryCode}
                >
                  {DESTINATION_OPTIONS.map((option) => (
                    <MenuItem disabled={!option.available} key={option.code} value={option.code}>
                      <WizardSelectOption option={option} />
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  label="Departure date"
                  onChange={(event) => setForm((current) => ({ ...current, departureDate: event.target.value }))}
                  type="date"
                  value={form.departureDate}
                />
                <TextField
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  label="Return date (optional)"
                  inputProps={{ min: form.departureDate || undefined }}
                  onChange={(event) => setForm((current) => ({ ...current, returnDate: event.target.value }))}
                  type="date"
                  value={form.returnDate}
                />
              </Stack>
            ) : null}

            {currentStep === 1 ? (
              members.length === 0 ? (
                <Alert severity="info">Add at least one family member before creating a trip.</Alert>
              ) : (
                <Stack spacing={2}>
                  {members.map((member) => {
                    const selected = Boolean(form.travelers[member.memberId]?.selected);
                    return (
                      <Paper key={member.memberId} sx={{ p: 2 }}>
                        <Stack spacing={2}>
                          <Stack
                            alignItems={{ xs: 'flex-start', md: 'center' }}
                            direction={{ xs: 'column', md: 'row' }}
                            justifyContent="space-between"
                            spacing={2}
                          >
                            <Box>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={selected}
                                    onChange={(event) => toggleTraveler(member.memberId, event.target.checked)}
                                  />
                                }
                                label={formatMemberName(member)}
                              />
                              <Typography color="text.secondary" sx={{ ml: 4.5 }} variant="body2">
                                {member.relationship} • Passport #{member.passportNumber}
                              </Typography>
                            </Box>
                            <TextField
                              disabled={!selected}
                              label="Visa type"
                              onChange={(event) => updateTravelerVisaType(member.memberId, event.target.value)}
                              select
                              sx={{ minWidth: { xs: '100%', md: 260 } }}
                              value={form.travelers[member.memberId]?.visaType ?? 'SchengenTourism'}
                            >
                              {VISA_TYPE_OPTIONS.map((option) => (
                                <MenuItem disabled={!option.available} key={option.value} value={option.value}>
                                  <WizardSelectOption option={option} />
                                </MenuItem>
                              ))}
                            </TextField>
                          </Stack>
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>
              )
            ) : null}

            {currentStep === 2 ? (
              selectedMembers.length === 0 ? (
                <Alert severity="info">Choose your travelers first, then document selection will unlock here.</Alert>
              ) : (
                <Stack spacing={2}>
                  {selectedMembers.map((member) => {
                    const memberDocuments = documentsByMemberId[member.memberId] ?? [];
                    const selectedDocuments = new Set(form.documentSelections[member.memberId] ?? []);

                    return (
                      <Paper key={member.memberId} sx={{ p: 2.25 }}>
                        <Stack spacing={1.5}>
                          <Box>
                            <Typography variant="h5">{formatMemberName(member)}</Typography>
                            <Typography color="text.secondary" sx={{ mt: 0.5 }} variant="body2">
                              {formatVisaType(form.travelers[member.memberId]?.visaType ?? 'SchengenTourism')}
                            </Typography>
                          </Box>
                          {memberDocuments.length === 0 ? (
                            <Alert severity="warning">This traveler has no documents in the library yet.</Alert>
                          ) : (
                            <Stack spacing={0.75}>
                              {memberDocuments.map((document) => (
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={selectedDocuments.has(document.documentId)}
                                      onChange={(event) =>
                                        toggleDocument(member.memberId, document.documentId, event.target.checked)
                                      }
                                    />
                                  }
                                  key={document.documentId}
                                  label={`${document.fileName} • ${document.documentType}`}
                                />
                              ))}
                            </Stack>
                          )}
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>
              )
            ) : null}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, width: '100%' }}>
              <Stack direction="row" spacing={1}>
                {currentStep > 0 ? (
                  <Button disabled={saving} onClick={() => setCurrentStep((value) => Math.max(value - 1, 0))} variant="text">
                    Back
                  </Button>
                ) : null}
                <Button disabled={saving} onClick={closeWizard} variant="text">
                  Cancel
                </Button>
                {currentStep < 2 ? (
                  <Button disabled={saving} onClick={handleContinue} variant="contained">
                    Continue
                  </Button>
                ) : (
                  <Button disabled={saving || selectedMemberIds.length === 0} onClick={handleCreateTrip} variant="contained">
                    {saving ? 'Creating...' : 'Create trip'}
                  </Button>
                )}
              </Stack>
            </Box>
          </SectionCard>
        </Box>
      )}

      <Dialog fullWidth maxWidth="md" onClose={closeRequirementDialog} open={requirementDialogOpen}>
        <DialogTitle>{activeRequirement?.title ?? 'Requirement details'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5}>
            {requirementDialogError ? <Alert severity="error">{requirementDialogError}</Alert> : null}

            <Paper sx={{ p: 2.5 }} variant="outlined">
              <Stack spacing={2}>
                <Typography variant="subtitle2">Requirement overview</Typography>
                <Stack spacing={1}>
                  <Typography color="text.secondary" variant="body2">
                    Traveler: {formatMemberName(activeRequirementMember ?? {}) || 'Unknown traveler'}
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    Type: {formatRequirementType(activeRequirement?.type)}
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    Description: {activeRequirement?.description || 'No description available yet.'}
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    Last updated: {formatDateTime(activeRequirement?.updatedAtUtc)}
                  </Typography>
                </Stack>

                {requirementSupportsAiValidation(activeRequirement) ? (
                  <Paper sx={{ p: 2 }} variant="outlined">
                    <Stack spacing={1.5}>
                      <Box
                        sx={{
                          alignItems: { xs: 'flex-start', sm: 'center' },
                          display: 'flex',
                          flexDirection: { xs: 'column', sm: 'row' },
                          gap: 1.5,
                          justifyContent: 'space-between',
                          width: '100%',
                        }}
                      >
                        <Typography variant="subtitle2">AI Validation</Typography>
                        <Button
                          disabled={Boolean(activeRequirementAiValidationTask)}
                          onClick={() => openAiValidationConfirmDialog(activeRequirement)}
                          sx={{
                            alignSelf: { xs: 'stretch', sm: 'flex-start' },
                            ml: { sm: 'auto' },
                          }}
                          variant="contained"
                        >
                          {activeRequirementAiValidationTask
                            ? 'Validation running...'
                            : activeRequirementLatestAiValidation
                              ? 'Run AI validation again'
                              : 'Validate with AI'}
                        </Button>
                      </Box>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <Chip
                          color={getRequirementAiValidationStatusColor(activeRequirement, Boolean(activeRequirementAiValidationTask))}
                          label={getRequirementAiValidationStatusLabel(activeRequirement, Boolean(activeRequirementAiValidationTask))}
                          size="small"
                          variant={activeRequirementLatestAiValidation || activeRequirementAiValidationTask ? 'filled' : 'outlined'}
                        />
                        {activeRequirementAiValidationTask ? (
                          <Typography color="text.secondary" variant="body2">
                            Started {formatDateTime(activeRequirementAiValidationTask.createdAtUtc)}
                          </Typography>
                        ) : activeRequirementLatestAiValidation?.timestamp ? (
                          <Typography color="text.secondary" variant="body2">
                            Last checked {formatDateTime(activeRequirementLatestAiValidation.timestamp)}
                          </Typography>
                        ) : null}
                      </Stack>
                      <Typography color="text.secondary" variant="body2">
                        {activeRequirementAiValidationTask?.message ||
                          activeRequirementLatestAiValidation?.rationale ||
                          'No AI validation has been run for this requirement yet.'}
                      </Typography>
                    </Stack>
                  </Paper>
                ) : null}

                {activeRequirementOnlineFormEntryPoint ? (
                  <Paper sx={{ p: 2 }} variant="outlined">
                    <Stack spacing={1}>
                      <Typography variant="subtitle2">Online Form Entry Point</Typography>
                      <Typography color="text.secondary" variant="body2">
                        Start the online visa form using the official link below.
                      </Typography>
                      <Button
                        href={activeRequirementOnlineFormEntryPoint}
                        rel="noreferrer"
                        startIcon={<OpenInNewOutlined />}
                        sx={{ alignSelf: 'flex-start' }}
                        target="_blank"
                        variant="outlined"
                      >
                        Open online form
                      </Button>
                    </Stack>
                  </Paper>
                ) : null}

                {activeRequirement?.type === 'Appointment' ? (
                  <Paper sx={{ p: 2 }} variant="outlined">
                    <Stack spacing={1.5}>
                      <Typography variant="subtitle2">Visa Appointment Request</Typography>
                      <Typography color="text.secondary" variant="body2">
                        Open the official portal yourself, or send us a visa appointment request that will be tracked in Orders with the normal details and confirmation flow.
                      </Typography>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <Button
                          disabled={!activeRequirementAppointmentEntryPoint}
                          href={activeRequirementAppointmentEntryPoint || undefined}
                          rel="noreferrer"
                          startIcon={<OpenInNewOutlined />}
                          sx={{ alignSelf: 'flex-start' }}
                          target="_blank"
                          variant="outlined"
                        >
                          Open appointment portal
                        </Button>
                        <Button
                          onClick={handleRequestVisaAppointmentOrder}
                          variant="contained"
                        >
                          Request visa appointment service
                        </Button>
                      </Stack>
                    </Stack>
                  </Paper>
                ) : null}

                {activeRequirement?.type === 'Photo' && activeRequirementPhotoSizeX && activeRequirementPhotoSizeY ? (
                  <Paper sx={{ p: 2 }} variant="outlined">
                    <Stack spacing={1.5}>
                      <Typography variant="subtitle2">Photo Size Requirement</Typography>
                      <Typography color="text.secondary" variant="body2">
                        Required photo size: {formatSizeValue(activeRequirementPhotoSizeX)} x {formatSizeValue(activeRequirementPhotoSizeY)}
                      </Typography>
                      <Typography color="text.secondary" variant="body2">
                        Printed photo option: 8 photos for 500 EGP + 85 EGP delivery.
                      </Typography>
                      <Button
                        disabled={!activeRequirementPrintablePhoto}
                        onClick={handleRequestPrintPhotosOrder}
                        sx={{ alignSelf: 'flex-start' }}
                        variant="contained"
                      >
                        Print 8 photos
                      </Button>
                      {!activeRequirementPrintablePhoto ? (
                        <Typography color="text.secondary" variant="caption">
                          Add or capture a photo first, then you can order printed copies from here.
                        </Typography>
                      ) : null}
                    </Stack>
                  </Paper>
                ) : null}

                {activeRequirement?.type === 'Document' && activeRequirementAcceptedLanguages.length > 0 ? (
                  <Paper sx={{ p: 2 }} variant="outlined">
                    <Stack spacing={1}>
                      <Typography variant="subtitle2">Accepted Languages</Typography>
                      <Stack direction="row" flexWrap="wrap" spacing={1} useFlexGap>
                        {activeRequirementAcceptedLanguages.map((language) => (
                          <Chip key={language} label={formatLanguageName(language)} size="small" variant="outlined" />
                        ))}
                      </Stack>
                    </Stack>
                  </Paper>
                ) : null}

                {activeRequirementTemplateDocuments.length > 0 ? (
                  <Paper sx={{ p: 2 }} variant="outlined">
                    <Stack spacing={1.25}>
                      <Typography variant="subtitle2">Template Documents</Typography>
                      <Typography color="text.secondary" variant="body2">
                        Download the provided template documents and complete them manually for this travel consent requirement.
                      </Typography>
                      <Stack spacing={1}>
                        {activeRequirementTemplateDocuments.map((templateDocument) => (
                          <Paper key={getTemplateDocumentId(templateDocument) || templateDocument.key} sx={{ p: 1.5 }} variant="outlined">
                            <Box
                              sx={{
                                alignItems: { xs: 'flex-start', sm: 'center' },
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                gap: 1,
                                justifyContent: 'space-between',
                                width: '100%',
                              }}
                            >
                              <Box>
                                <Typography sx={{ fontWeight: 600 }}>
                                  {templateDocument.name || 'Template document'}
                                </Typography>
                                <Typography color="text.secondary" variant="body2">
                                  Ready for download
                                </Typography>
                              </Box>
                              <Stack
                                sx={{
                                  ml: { sm: 'auto' },
                                  width: { xs: '100%', sm: 'auto' },
                                }}
                              >
                                <Button
                                  disabled={templateDownloadBusyKey === templateDocument.key}
                                  onClick={() => handleTemplateDownload(templateDocument)}
                                  startIcon={<OpenInNewOutlined />}
                                  sx={{ justifyContent: 'center' }}
                                  variant="outlined"
                                >
                                  {templateDownloadBusyKey === templateDocument.key ? 'Preparing...' : 'Download'}
                                </Button>
                              </Stack>
                            </Box>
                          </Paper>
                        ))}
                      </Stack>
                    </Stack>
                  </Paper>
                ) : null}

                {activeRequirement?.officialLink ? (
                  <Button
                    href={activeRequirement.officialLink}
                    rel="noreferrer"
                    startIcon={<OpenInNewOutlined />}
                    sx={{ alignSelf: 'flex-start', mt: 1 }}
                    target="_blank"
                    variant="outlined"
                  >
                    Open official source
                  </Button>
                ) : null}
                {activeRequirement?.offlineAttachment ? (
                  <Typography color="text.secondary" variant="body2">
                    Offline template key: {activeRequirement.offlineAttachment}
                  </Typography>
                ) : null}
              </Stack>
            </Paper>

            <Paper sx={{ p: 2.5 }} variant="outlined">
              <Stack spacing={1.5}>
                <Box
                  sx={{
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 1,
                    width: '100%',
                  }}
                >
                  <Typography variant="subtitle2">Linked documents</Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ ml: { sm: 'auto' } }}>
                    {activeRequirement?.type === 'Photo' ? (
                      <Button
                        disabled={requirementUploadBusy || requirementLinkBusy || cameraBusy || !activeRequirement}
                        onClick={handleTakePhotoClick}
                        size="small"
                        sx={{
                          alignSelf: { xs: 'flex-start', sm: 'center' },
                          minWidth: 'fit-content',
                        }}
                        variant="outlined"
                      >
                        {cameraBusy ? 'Capturing...' : 'Take Photo'}
                      </Button>
                    ) : null}
                    {activeRequirement?.type !== 'Photo' ? (
                      <Button
                        disabled={requirementUploadBusy || requirementLinkBusy || cameraBusy || !activeRequirement}
                        onClick={openRequirementLibraryDialog}
                        size="small"
                        sx={{
                          alignSelf: { xs: 'flex-start', sm: 'center' },
                          minWidth: 'fit-content',
                        }}
                        variant="outlined"
                      >
                        Link Existing
                      </Button>
                    ) : null}
                    {activeRequirement?.type !== 'Photo' ? (
                      <Button
                        disabled={requirementUploadBusy || requirementLinkBusy || cameraBusy || !activeRequirement}
                        onClick={() => requirementUploadInputRef.current?.click()}
                        startIcon={<AddOutlined />}
                        size="small"
                        sx={{
                          alignSelf: { xs: 'flex-start', sm: 'center' },
                          color: 'text.secondary',
                          justifyContent: 'flex-end',
                          minWidth: 'fit-content',
                          px: 0.5,
                        }}
                        variant="text"
                      >
                        {requirementUploadBusy ? 'Uploading...' : 'Add Document'}
                      </Button>
                    ) : null}
                  </Stack>
                </Box>

                {sortedActiveRequirementDocuments.length ? (
                  <Stack spacing={1}>
                    {sortedActiveRequirementDocuments.map((document) => {
                      return (
                        <Paper
                          key={document.documentId}
                          onClick={() => openLinkedDocumentDialog(document)}
                          sx={{
                            cursor: 'pointer',
                            p: 1.5,
                            transition: 'border-color 120ms ease, box-shadow 120ms ease',
                            '&:hover': {
                              borderColor: 'primary.main',
                              boxShadow: '0 8px 20px rgba(14, 28, 45, 0.08)',
                            },
                          }}
                          variant="outlined"
                        >
                          <Stack
                            alignItems={{ xs: 'flex-start', sm: 'center' }}
                            direction={{ xs: 'column', sm: 'row' }}
                            justifyContent="space-between"
                            spacing={1}
                          >
                            <Box>
                              <Typography sx={{ fontWeight: 600 }}>{document.fileName}</Typography>
                              <Typography color="text.secondary" variant="body2">
                                {formatDocumentType(document.documentType)} • Uploaded {formatDateTime(document.updatedAtUtc)}
                              </Typography>
                              {activeRequirement?.type === 'Document' ? (
                                <Link
                                  component="a"
                                  href="#request-translation-order"
                                  onClick={(event) => handleRequestTranslationOrder(event, document)}
                                  sx={{
                                    color: 'primary.main',
                                    display: 'inline-block',
                                    fontWeight: 600,
                                    mt: 0.5,
                                  }}
                                  underline="hover"
                                >
                                  Request notarised translation
                                </Link>
                              ) : null}
                              {activeRequirement?.type === 'Photo' && isImageDocument(document) ? (
                                <Link
                                  component="a"
                                  href="#request-print-photos-order"
                                  onClick={(event) => handleRequestPrintPhotosOrder(event, document)}
                                  sx={{
                                    color: 'primary.main',
                                    display: 'inline-block',
                                    fontWeight: 600,
                                    mt: 0.5,
                                  }}
                                  underline="hover"
                                >
                                  Print 8 photos
                                </Link>
                              ) : null}
                            </Box>
                            <Stack alignItems="center" direction="row" spacing={0.5}>
                              <Tooltip title="Unlink document">
                                <span>
                                  <IconButton
                                    aria-label={`unlink ${document.fileName}`}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      openLinkedDocumentAction('unlink', document);
                                    }}
                                    size="small"
                                  >
                                    <LinkOffOutlined fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Delete document">
                                <span>
                                  <IconButton
                                    aria-label={`delete ${document.fileName}`}
                                    color="error"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      openLinkedDocumentAction('delete', document);
                                    }}
                                    size="small"
                                  >
                                    <DeleteOutlined fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Stack>
                          </Stack>
                        </Paper>
                      );
                    })}
                  </Stack>
                ) : (
                  <Typography color="text.secondary" variant="body2">
                    No documents are linked to this requirement yet.
                  </Typography>
                )}
              </Stack>
            </Paper>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeRequirementDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      <DocumentDetailsDialog
        account={account}
        document={linkedDocumentDialogDocument}
        members={members}
        onClose={closeLinkedDocumentDialog}
        onSaved={handleLinkedDocumentSaved}
        open={Boolean(linkedDocumentDialogDocument)}
      />

      <Dialog fullWidth maxWidth="xs" onClose={closeAiValidationConfirmDialog} open={Boolean(aiValidationConfirmTarget)}>
        <DialogTitle>Start AI validation?</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            {aiValidationError ? <Alert severity="error">{aiValidationError}</Alert> : null}
            <Typography color="text.secondary" variant="body2">
              AI will review the linked files for{' '}
              <strong>{aiValidationConfirmTarget?.title ?? 'this requirement'}</strong> and store the latest verdict and
              rationale on the trip requirement.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAiValidationConfirmDialog}>
            Cancel
          </Button>
          <Button
            disabled={Boolean(
              aiValidationConfirmTarget &&
                getRequirementAiValidationTask(aiValidationTasks, selectedTrip?.tripId, aiValidationConfirmTarget)
            )}
            onClick={handleConfirmAiValidation}
            variant="contained"
          >
            {aiValidationConfirmTarget &&
            getRequirementAiValidationTask(aiValidationTasks, selectedTrip?.tripId, aiValidationConfirmTarget)
              ? 'Already running'
              : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        fullWidth
        maxWidth="sm"
        onClose={closeRequirementLibraryDialog}
        open={requirementLibraryDialogOpen}
      >
        <DialogTitle>Link existing document</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {requirementLibraryError ? <Alert severity="error">{requirementLibraryError}</Alert> : null}
            <Typography color="text.secondary" variant="body2">
              Choose an existing item from this traveler&apos;s document library to link it to this requirement.
            </Typography>
            {availableRequirementLibraryDocuments.length > 0 ? (
              <Stack spacing={1}>
                {availableRequirementLibraryDocuments.map((document) => (
                  <Paper key={document.documentId} sx={{ p: 1.5 }} variant="outlined">
                    <Stack
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                      direction={{ xs: 'column', sm: 'row' }}
                      justifyContent="space-between"
                      spacing={1}
                    >
                      <Box>
                        <Typography sx={{ fontWeight: 600 }}>{document.fileName}</Typography>
                        <Typography color="text.secondary" variant="body2">
                          {formatDocumentType(document.documentType)} • Uploaded {formatDateTime(document.updatedAtUtc)}
                        </Typography>
                      </Box>
                      <Button
                        disabled={requirementLinkBusy}
                        onClick={() => handleLinkExistingRequirementDocument(document)}
                        size="small"
                        variant="contained"
                      >
                        {requirementLinkBusy ? 'Linking...' : 'Link'}
                      </Button>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            ) : (
              <Alert severity="info">
                {activeRequirement?.documents?.length
                  ? 'All library documents for this traveler are already linked here.'
                  : 'No existing documents are available in this traveler\'s library yet.'}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button disabled={requirementLinkBusy} onClick={closeRequirementLibraryDialog}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog fullWidth maxWidth="xs" onClose={closeLinkedDocumentActionDialog} open={Boolean(linkedDocumentActionTarget)}>
        <DialogTitle>
          {linkedDocumentActionType === 'unlink' ? 'Unlink document' : 'Delete document'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {linkedDocumentActionError ? <Alert severity="error">{linkedDocumentActionError}</Alert> : null}
            <Typography color="text.secondary" variant="body2">
              {linkedDocumentActionType === 'unlink'
                ? `Unlink ${linkedDocumentActionTarget?.fileName || 'this document'} from this requirement? The document will stay saved for this traveler.`
                : `Delete ${linkedDocumentActionTarget?.fileName || 'this document'} everywhere? This removes the stored file and unlinks it from any requirement that uses it.`}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button disabled={linkedDocumentActionBusy} onClick={closeLinkedDocumentActionDialog}>
            Cancel
          </Button>
          <Button
            color={linkedDocumentActionType === 'delete' ? 'error' : 'primary'}
            disabled={linkedDocumentActionBusy}
            onClick={handleConfirmLinkedDocumentAction}
            variant="contained"
          >
            {linkedDocumentActionBusy
              ? linkedDocumentActionType === 'unlink'
                ? 'Unlinking...'
                : 'Deleting...'
              : linkedDocumentActionType === 'unlink'
                ? 'Unlink'
                : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog fullWidth maxWidth="xs" onClose={closeCameraChoiceDialog} open={cameraChoiceDialogOpen}>
        <DialogTitle>Take Photo</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography color="text.secondary" variant="body2">
              You’re on a desktop device. Choose whether to use this computer or switch to your phone for the capture.
            </Typography>
            <Button fullWidth onClick={handleChooseDesktopCamera} startIcon={<LaptopMacOutlined />} variant="outlined">
              Use this computer
            </Button>
            <Button fullWidth onClick={handleChoosePhoneCamera} startIcon={<PhoneIphoneOutlined />} variant="contained">
              Use my phone
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeCameraChoiceDialog}>Cancel</Button>
        </DialogActions>
      </Dialog>

      <Dialog fullWidth maxWidth="sm" onClose={closeLivePhotoDialog} open={livePhotoDialogOpen}>
        <DialogTitle>Take Photo From Your Phone</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5}>
            {livePhotoError ? <Alert severity="error">{livePhotoError}</Alert> : null}
            {livePhotoBusy ? (
              <Stack alignItems="center" spacing={1.5} sx={{ py: 6 }}>
                <CircularProgress size={28} />
                <Typography variant="body2">Preparing your phone capture link...</Typography>
              </Stack>
            ) : livePhotoSession ? (
              <>
                {livePhotoStatus?.isExpired ? (
                  <Stack spacing={1.5}>
                    <Alert severity="warning">This live photo session expired. Generate a new QR code to continue.</Alert>
                    <Typography color="text.secondary" variant="caption">
                      Expired at {formatDateTime(livePhotoStatus.expiresAtUtc)}
                    </Typography>
                  </Stack>
                ) : livePhotoStatus?.hasUploadedPhoto ? (
                  <Stack spacing={1.5}>
                    <Alert severity="success">
                      A photo was received from your phone. Review it here, or choose Try Again to capture a new one.
                    </Alert>
                    {livePhotoStatus.previewUrl ? (
                      <Box
                        component="img"
                        src={livePhotoStatus.previewUrl}
                        alt="Latest phone photo preview"
                        sx={{
                          bgcolor: '#111',
                          borderRadius: 2,
                          display: 'block',
                          mx: 'auto',
                          objectFit: 'cover',
                          width: '100%',
                          maxWidth: 420,
                        }}
                      />
                    ) : null}
                    <Typography color="text.secondary" variant="caption">
                      Last updated {formatDateTime(livePhotoStatus.uploadedAtUtc)}
                    </Typography>
                  </Stack>
                ) : (
                  <Stack spacing={2}>
                    <Typography color="text.secondary" variant="body2">
                      Scan this QR code from your phone. We&apos;ll keep listening here and switch to the received photo as
                      soon as it arrives.
                    </Typography>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={2}
                      sx={{ alignItems: 'stretch', justifyContent: 'center' }}
                    >
                      <Stack
                        alignItems="center"
                        spacing={1.5}
                        sx={{
                          bgcolor: '#fff',
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 3,
                          flex: '0 0 auto',
                          p: 2,
                        }}
                      >
                        <QRCodeCanvas size={220} value={livePhotoMobileUrl} />
                        <Button
                          component="a"
                          href={livePhotoMobileUrl || undefined}
                          rel="noreferrer"
                          startIcon={<OpenInNewOutlined />}
                          target="_blank"
                          variant="text"
                        >
                          Open link directly
                        </Button>
                        <Typography color="text.secondary" variant="caption">
                          Expires at {formatDateTime(livePhotoStatus?.expiresAtUtc ?? livePhotoSession.expiresAtUtc)}
                        </Typography>
                      </Stack>
                      <Stack
                        alignItems="center"
                        justifyContent="center"
                        spacing={1.5}
                        sx={{
                          border: '1px dashed',
                          borderColor: 'divider',
                          borderRadius: 3,
                          flex: 1,
                          minHeight: 220,
                          px: 3,
                          py: 4,
                          textAlign: 'center',
                        }}
                      >
                        <CircularProgress size={28} />
                        <Typography sx={{ fontWeight: 600 }} variant="body2">
                          Waiting for a photo from your phone...
                        </Typography>
                        <Typography color="text.secondary" variant="body2">
                          Once a photo is received, this QR code will be hidden and the preview will appear here.
                        </Typography>
                      </Stack>
                    </Stack>
                  </Stack>
                )}
              </>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button disabled={livePhotoBusy || livePhotoFinalizeBusy} onClick={closeLivePhotoDialog}>
            Cancel
          </Button>
          {livePhotoStatus?.hasUploadedPhoto && !livePhotoStatus?.isExpired ? (
            <Button
              disabled={livePhotoBusy || livePhotoFinalizeBusy}
              onClick={handleRefreshLivePhotoSession}
              startIcon={<RefreshOutlined />}
            >
              Try Again
            </Button>
          ) : (
            <Button
              disabled={livePhotoBusy || livePhotoFinalizeBusy}
              onClick={handleRefreshLivePhotoSession}
              startIcon={<RefreshOutlined />}
            >
              {livePhotoStatus?.isExpired ? 'Refresh QR' : 'New QR'}
            </Button>
          )}
          <Button
            disabled={livePhotoBusy || livePhotoFinalizeBusy || !livePhotoStatus?.hasUploadedPhoto || livePhotoStatus?.isExpired}
            onClick={handleFinalizeLivePhoto}
            variant="contained"
          >
            {livePhotoFinalizeBusy ? 'Using Photo...' : 'Use This Photo'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog fullWidth maxWidth="sm" onClose={closeCameraDialog} open={cameraDialogOpen}>
        <DialogTitle>Take Photo</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {cameraError ? <Alert severity="error">{cameraError}</Alert> : null}
            {cameraBusy ? (
              <Stack alignItems="center" spacing={1.5} sx={{ py: 6 }}>
                <CircularProgress size={28} />
                <Typography sx={{ fontWeight: 600 }} variant="body2">
                  Validating your image meets visa requirements. Please hold...
                </Typography>
              </Stack>
            ) : cameraCapturedPreviewUrl ? (
              <Stack spacing={2}>
                <Typography color="text.secondary" variant="body2">
                  Review this photo before we validate it against the visa requirement.
                </Typography>
                <Box
                  component="img"
                  src={cameraCapturedPreviewUrl}
                  alt="Captured visa photo preview"
                  sx={{
                    bgcolor: '#111',
                    borderRadius: 2,
                    display: 'block',
                    mx: 'auto',
                    objectFit: 'cover',
                    width: '100%',
                    maxWidth: 420,
                    aspectRatio: `${activeRequirementPhotoAspectRatio}`,
                  }}
                />
              </Stack>
            ) : (
              <>
                <Typography color="text.secondary" variant="body2">
                  Center your face inside the frame, keep your head upright, and use even lighting with a light background.
                </Typography>
                <Box
                  sx={{
                    bgcolor: '#111',
                    borderRadius: 2,
                    mx: 'auto',
                    overflow: 'hidden',
                    position: 'relative',
                    width: '100%',
                    maxWidth: 420,
                    aspectRatio: `${activeRequirementPhotoAspectRatio}`,
                  }}
                >
                  {!cameraReady ? (
                    <Stack
                      alignItems="center"
                      justifyContent="center"
                      spacing={1}
                      sx={{
                        color: 'rgba(255,255,255,0.88)',
                        inset: 0,
                        position: 'absolute',
                        zIndex: 1,
                      }}
                    >
                      <CircularProgress color="inherit" size={22} />
                      <Typography color="inherit" variant="body2">
                        Starting camera preview...
                      </Typography>
                    </Stack>
                  ) : null}
                  <video
                    autoPlay
                    muted
                    onCanPlay={() => setCameraReady(true)}
                    onLoadedData={() => setCameraReady(true)}
                    playsInline
                    ref={cameraVideoRef}
                    style={{
                      display: 'block',
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transform: cameraFacingMode === 'user' ? 'scaleX(-1)' : 'none',
                    }}
                  />
                  <Tooltip title={`Use ${cameraFacingMode === 'user' ? 'back' : 'front'} camera`}>
                    <span>
                      <IconButton
                        aria-label={`Use ${cameraFacingMode === 'user' ? 'back' : 'front'} camera`}
                        disabled={cameraBusy}
                        onClick={handleFlipCamera}
                        size="small"
                        sx={{
                          bgcolor: 'rgba(0,0,0,0.56)',
                          color: '#fff',
                          position: 'absolute',
                          right: 12,
                          top: 12,
                          zIndex: 3,
                          '&:hover': { bgcolor: 'rgba(0,0,0,0.72)' },
                        }}
                      >
                        <CameraswitchOutlined fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Box
                    sx={{
                      border: '2px solid rgba(255,255,255,0.92)',
                      borderRadius: 2,
                      boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.32)',
                      inset: 0,
                      pointerEvents: 'none',
                      position: 'absolute',
                      zIndex: 2,
                    }}
                  />
                </Box>
                <Typography color="text.secondary" sx={{ textAlign: 'center' }} variant="caption">
                  The captured photo will match the target aspect ratio shown here.
                </Typography>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          {cameraCapturedPreviewUrl ? (
            <>
              <Button disabled={cameraBusy} onClick={handleRetryCapturedPhoto}>
                Retry
              </Button>
              <Button disabled={cameraBusy} onClick={handleProceedCapturedPhoto} variant="contained">
                Proceed
              </Button>
            </>
          ) : (
            <>
              <Button disabled={cameraBusy} onClick={closeCameraDialog}>
                Cancel
              </Button>
              <Button disabled={cameraBusy || !cameraStream || !cameraReady} onClick={handleCapturePhoto} variant="contained">
                Capture Photo
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        onClose={() => {
          if (!deleteBusy) {
            setDeleteError('');
            setDeleteTarget(null);
          }
        }}
        open={Boolean(deleteTarget)}
      >
        <DialogTitle>Delete trip</DialogTitle>
        <DialogContent sx={{ pt: '10px !important' }}>
          <Stack spacing={1.5}>
            {deleteError ? <Alert severity="error">{deleteError}</Alert> : null}
            <Typography variant="body2">
              Delete the trip to {deleteTarget?.destinationCountryName || 'this destination'} scheduled for{' '}
              {deleteTarget ? formatTripDateRange(deleteTarget) : 'these dates'}?
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            disabled={deleteBusy}
            onClick={() => {
              setDeleteError('');
              setDeleteTarget(null);
            }}
            variant="text"
          >
            Cancel
          </Button>
          <Button color="error" disabled={deleteBusy} onClick={handleConfirmDeleteTrip} variant="contained">
            {deleteBusy ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

export default TripsWorkspace;
